// The ServiceWalletRegistry deployments this FE talks to, and the one function
// it calls on them.
//
// IN-CODE ONLY: never read these from env (`import.meta.env`) or any runtime
// config. A registry address decides where a user's own wallet records what a
// service wallet is allowed to bridge on their behalf, so it must not be
// something a served response can move.
//
// The registry is the contract that already says what service wallets may do.
// Since 1_02_00 it also stores USER-registered bridge intents: before the
// backend may push a withdrawal's funds into the bridge, the user's own EOA
// calls `registerBridgeIntent` here, pinning the bridge request id together
// with token, amount, origin/destination chain and recipient. RustSigner reads
// that entry over RPC and refuses to sign the bridge deposit unless the
// calldata matches it.
//
// LOCKSTEP: MoonHub.API pins the same address (its config site:
// MoonHub.API/access/EvmChainAccess.ts, `Config[25].ServiceWalletRegistry`) and
// RustSigner does not pin it at all -- it resolves it on chain from the
// CustodialWalletManager (`CWM.serviceWalletRegistry()`). Changing the
// deployment therefore means updating this map AND MoonHub.API together, and
// pointing the CWM at the new registry.
//
// A registry lives on each AUTHORIZATION ANCHOR (see
// features/wallet/config/anchors.ts): the user's wallet is already there, and
// the bridge origin chain (HyperEVM, 999) is not somewhere we want to push users
// for a signature. The 999 deployment exists but stores no intents. RustSigner
// and MoonHub.API accept an intent from EITHER anchor's registry, so a
// withdrawal registers on the anchor the session is running under.

/** Anchors whose ServiceWalletRegistry can hold a withdrawal's bridge intent. */
export const SERVICE_WALLET_REGISTRY_CHAIN_IDS = [25, 4663] as const;

/** Cronos, the default anchor; kept for callers that predate the second one. */
export const SERVICE_WALLET_REGISTRY_CHAIN_ID = 25;

export const ZERO_SERVICE_WALLET_REGISTRY =
    '0x0000000000000000000000000000000000000000' as const;

export const SERVICE_WALLET_REGISTRIES: Record<number, `0x${string}`> = {
    25: '0xD4ee1B06A0C90B33ccd924Eb5020322eFd87271F',
    // Robinhood Chain, from SmartContracts.Sensitive/deploy/data/deployments.csv
    // (4663). A chain listed here before its registry is deployed keeps the zero
    // address as a tripwire: `serviceWalletRegistryFor` throws rather than
    // sending a registration to nowhere.
    4663: '0x52320EDeB61D41D574c94C8059ffC22ADF6D7c05',
};

export const CRONOS_SERVICE_WALLET_REGISTRY =
    SERVICE_WALLET_REGISTRIES[SERVICE_WALLET_REGISTRY_CHAIN_ID]!;

export function hasServiceWalletRegistry(chainId: number): boolean {
    const registry = SERVICE_WALLET_REGISTRIES[chainId];
    return (
        typeof registry === 'string' &&
        registry.toLowerCase() !== ZERO_SERVICE_WALLET_REGISTRY
    );
}

/**
 * The registry a withdrawal anchored on `chainId` registers its bridge intent
 * in. Throws for a chain with no deployment: a registration sent to the zero
 * address would leave the user paying gas for a hop the signer then refuses.
 */
export function serviceWalletRegistryFor(chainId: number): `0x${string}` {
    if (!hasServiceWalletRegistry(chainId)) {
        throw new Error(
            `No ServiceWalletRegistry is deployed on chain ${chainId} yet, ` +
                'so a withdrawal cannot be recorded there.',
        );
    }
    return SERVICE_WALLET_REGISTRIES[chainId]!;
}

/**
 * `bridgeId` namespaces the intent store per bridge, so a second bridge is a
 * new id plus its own calldata decoder rather than a storage change. 1 is
 * Relay, 2 is deBridge (DLN), 3 is LI.FI; the backend's bridge registry
 * (`GET /speed/bridges`) decides which of them a withdrawal is quoted on.
 * Every consumer (this app, MoonHub.API, RustSigner) carries the same numbers.
 *
 * The list is a WHITELIST, not documentation: an intent naming any other id
 * would be recorded in a namespace no signer reads, so the user would pay gas
 * for a registration that authorizes nothing.
 */
export const SUPPORTED_BRIDGE_IDS = [1, 2, 3] as const;

/**
 * The entrypoint the user's wallet calls, plus the read that makes a repeat
 * registration safe.
 *
 * `getBridgeIntent` is here for exactly one job: the registry is write-once per
 * (owner, bridgeId, id), so a second registration of the SAME hop reverts with
 * "already registered". That is not a failure, it is the hop already being
 * authorized, but only if what is stored is the hop we were about to sign. The
 * app reads the entry back and compares it before treating the revert as
 * success; the backend and the signer still do their own reads.
 */
/**
 * The first registry release that knows bridge intents at all. An older proxy
 * has no `registerBridgeIntent`, so a registration against it reverts with no
 * reason (observed on Cronos, 2026-09-02, at 1_01_00): read `version()` first
 * and say so, instead of having the user sign a doomed transaction.
 */
export const MIN_BRIDGE_INTENT_REGISTRY_VERSION = 10_200n;

export const SERVICE_WALLET_REGISTRY_ABI = [
    {
        type: 'function',
        name: 'version',
        stateMutability: 'pure',
        inputs: [],
        outputs: [{ type: 'uint256' }],
    },
    {
        type: 'function',
        name: 'registerBridgeIntent',
        stateMutability: 'nonpayable',
        inputs: [
            { name: 'bridgeId', type: 'uint32' },
            { name: 'id', type: 'bytes32' },
            { name: 'originChainId', type: 'uint64' },
            { name: 'token', type: 'address' },
            { name: 'amount', type: 'uint256' },
            { name: 'destinationChainId', type: 'uint64' },
            { name: 'recipient', type: 'address' },
        ],
        outputs: [],
    },
    {
        type: 'function',
        name: 'getBridgeIntent',
        stateMutability: 'view',
        inputs: [
            { name: 'owner', type: 'address' },
            { name: 'bridgeId', type: 'uint32' },
            { name: 'id', type: 'bytes32' },
        ],
        outputs: [
            {
                type: 'tuple',
                components: [
                    { name: 'originChainId', type: 'uint64' },
                    { name: 'destinationChainId', type: 'uint64' },
                    { name: 'registeredAt', type: 'uint64' },
                    { name: 'token', type: 'address' },
                    { name: 'recipient', type: 'address' },
                    { name: 'amount', type: 'uint256' },
                ],
            },
        ],
    },
] as const;
